export interface SplitDocsBody {
  /** Markdown shown before the video. */
  intro: string
  /** Markdown shown after the video. */
  rest: string
}

const VIDEO_MARKER = /^[ \t]*\[\/\/\]:[ \t]*#[ \t]*\([ \t]*video[ \t]*\)[ \t]*$/i
const HEADING = /^ {0,3}#{1,6}\s/
const FENCE = /^ {0,3}(```|~~~)/

/**
 * Decides where the article video goes inside the markdown body.
 *  1. An explicit `[//]: # (video)` line wins. It is the standard markdown comment (a link reference definition), so
 *     it renders as nothing in every markdown renderer, including the full Docs page.
 *  2. Otherwise the video goes before the first heading, which is right after the opening paragraphs.
 *  3. A body without headings gets the video after its first paragraph.
 */
export const splitDocsBodyForVideo = (body: string): SplitDocsBody => {
  const lines = body.split('\n')
  let isInFence = false
  let headingIndex = -1
  let firstParagraphEnd = -1
  let hasSeenText = false

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]

    if (FENCE.test(line)) {
      isInFence = !isInFence
      hasSeenText = true
      continue
    }
    if (isInFence) continue

    if (VIDEO_MARKER.test(line)) {
      return {
        intro: lines.slice(0, index).join('\n').trimEnd(),
        rest: lines
          .slice(index + 1)
          .join('\n')
          .trimStart(),
      }
    }
    if (headingIndex === -1 && HEADING.test(line)) headingIndex = index

    if (line.trim() !== '') {
      hasSeenText = true
    } else if (hasSeenText && firstParagraphEnd === -1) {
      firstParagraphEnd = index
    }
  }

  const splitAt = headingIndex !== -1 ? headingIndex : firstParagraphEnd !== -1 ? firstParagraphEnd : lines.length
  return {
    intro: lines.slice(0, splitAt).join('\n').trimEnd(),
    rest: lines.slice(splitAt).join('\n').trimStart(),
  }
}
